import _ from 'lodash';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import qs from 'qs';
import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';

import { ErrorHandler, CenteredLoader as Loader } from '@playbookux/playbook-web-core';
import {
  setTestSession,
  setProjectValues,
  clearQuestions,
  clearSurveyQuestions,
  setSurveyTestSession,
  setSurveyProjectValues,
} from '../../../core/actions';
import TEST_TYPE from '../../../core/constants/testTypes';

import { TEST_SESSION } from './graphql';

import {
  Button,
  Segment,
  Text,
  Title,
} from './styles';
import { Container, Message } from '../recruit-participants/styles';

const WelcomePage = (props) => {
  const {
    history = {},
    location = {},
    onClearQuestions,
    onClearSurveyQuestions,
    onSetSurveyTestSession,
    onSetSurveyProject,
    onSetTestSession,
    onSetProject,
  } = props;

  const { search } = location;
  const { 'session-id': sessionId = '' } = qs.parse(search, { ignoreQueryPrefix: true });

  const {
    data = {},
    error,
    loading,
  } = useQuery(TEST_SESSION, {
    variables: { id: sessionId },
    skip: _.isEmpty(sessionId),
  });

  const { testSession = {} } = data;
  const { tester = {}, isPreview = false } = testSession;
  const { project = {} } = tester;

  const {
    id: projectId,
    testType,
    welcomeContent: message,
    welcomeTitle: title,
  } = project;

  const buttonText = testType === TEST_TYPE.TREE_TEST ? 'Next' : 'Proceed to Study';

  const isPreviewStudy = testSession && testSession.isPreview;

  const handleClick = _.debounce(() => {
    switch (testType) {
      case TEST_TYPE.TREE_TEST:
        history.push('/instructions-tester');
        break;
      case TEST_TYPE.CARD_SORT:
        history.push(`/card-sort?projectId=${projectId}&session-id=${sessionId}`);
        break;
      case TEST_TYPE.SURVEY:
        history.push(`/survey?isPreviewStudy=${isPreviewStudy}`);
        break;
      default:
        break;
    }
  }, 100);

  useEffect(() => {
    onClearQuestions();
    onClearSurveyQuestions();
  }, [onClearQuestions, onClearSurveyQuestions]);

  // Set preview study on redux
  useEffect(() => {
    const session = { id: testSession.id };
    if (isPreview && sessionId && !_.isEmpty(testSession)) {
      onSetTestSession(session, isPreview);
    }

    if (sessionId && !_.isEmpty(testSession)) {
      onSetSurveyTestSession(session, isPreview);
    }

    if (!_.isEmpty(project)) {
      onSetSurveyProject(project);
      onSetProject(project);
    }
  }, [isPreview, onSetProject, onSetSurveyProject, onSetSurveyTestSession, onSetTestSession, project, sessionId, testSession]);

  if (loading) {
    return (
      <Loader data-loader="welcome-loader" />
    );
  }

  if (error) {
    return (
      <ErrorHandler
        error={error}
        defaultMessage="Something went wrong."
      />
    );
  }

  if (!isPreviewStudy && (testSession && new Date(testSession.expiresAt) < new Date())) {
    return (
      <Container center>
        <Message>Test session expired</Message>
      </Container>
    );
  }

  return (
    <Segment>
      <>
        <Title>{title}</Title>
        <Text>{message}</Text>

        <Button onClick={handleClick} data-tester-test-proceed>
          {buttonText}
        </Button>
      </>
    </Segment>
  );
};

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    onSetTestSession: setTestSession,
    onSetProject: setProjectValues,
    onClearQuestions: clearQuestions,
    onClearSurveyQuestions: clearSurveyQuestions,
    onSetSurveyTestSession: setSurveyTestSession,
    onSetSurveyProject: setSurveyProjectValues,
  },
  dispatch,
);

const enhance = compose(
  connect(null, mapDispatchToProps),
);

WelcomePage.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    loading: PropTypes.bool,
  }).isRequired,
  onSetTestSession: PropTypes.func.isRequired,
  onSetSurveyTestSession: PropTypes.func.isRequired,
  onSetProject: PropTypes.func.isRequired,
  onSetSurveyProject: PropTypes.func.isRequired,
  onClearQuestions: PropTypes.func.isRequired,
  onClearSurveyQuestions: PropTypes.func.isRequired,
  project: PropTypes.shape({
    id: PropTypes.string,
    testType: PropTypes.string,
    welcomeContent: PropTypes.string,
    welcomeTitle: PropTypes.string,
  }),
};

WelcomePage.defaultProps = {
  project: {},
};

export default enhance(WelcomePage);
